/**
 * Project Runs sub-page — runs for the project selected in the context bar.
 * The context bar's selected run drives which run's form is open; selecting a
 * run from the list also updates that shared selection.
 */

import { useState } from 'react';
import { Stack, useTheme } from '@fti/design-system';
import {
  useRuns,
  useCreateRun,
  useValidateRun,
} from '../../../api/media-sim/runs';
import { useMessages } from '../../../api/media-sim/messages';
import { useProjectPublications } from '../../../api/media-sim/projects';
import { RunList } from '../../../components/media-sim/runs/RunList';
import { RunForm } from '../../../components/media-sim/runs/RunForm';
import { useProjectRun } from '../../../components/media-sim/layout/ProjectRunContext';
import { ProjectScopeGate } from './ProjectScopeGate';
import type { Project, RunValidationResult } from '../../../types/media-sim';

function RunsContent({ project }: { project: Project }) {
  const { theme } = useTheme();
  const projectId = project.id;
  const { selectedRunId, selectRun } = useProjectRun();

  const { data: runs, refetch: refetchRuns } = useRuns(projectId);
  const { data: publications } = useProjectPublications(projectId);
  const { data: registeredMessages } = useMessages(projectId, { status: 'registered' });
  const { createRun } = useCreateRun();
  const { validateRun } = useValidateRun();
  const [runValidation, setRunValidation] = useState<RunValidationResult | undefined>();

  // Only treat the context-bar run as "open" if it belongs to this project.
  const openRunId = runs?.some((r) => r.id === selectedRunId) ? selectedRunId : null;

  const handleCreateRun = async () => {
    try {
      const run = await createRun(projectId, { run_name: 'New Run', number_of_articles: 5 });
      if (run) { refetchRuns(); selectRun(run.id); }
    } catch { /* handled by error state */ }
  };

  const handleValidateRun = async () => {
    if (!openRunId) return;
    try {
      const result = await validateRun(projectId, openRunId);
      if (result) setRunValidation(result);
    } catch { /* handled by error state */ }
  };

  return (
    <Stack gap="md">
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
        Runs — {project.project_name}
      </h1>
      {openRunId ? (
        <RunForm
          projectId={projectId}
          runId={openRunId}
          projectPublications={publications || []}
          projectMessages={registeredMessages || []}
          onBack={() => { selectRun(null); refetchRuns(); }}
          onValidate={handleValidateRun}
          validationResult={runValidation}
        />
      ) : (
        <RunList
          runs={runs || []}
          onSelect={(id) => selectRun(id)}
          onCreate={handleCreateRun}
        />
      )}
    </Stack>
  );
}

export function ProjectRunsPage() {
  return <ProjectScopeGate>{(project) => <RunsContent project={project} />}</ProjectScopeGate>;
}
